import * as THREE from 'three';
import { TerrainBounds, TerrainType, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Abstract base class for all terrain features.
 * Implements height sampling and visual mesh generation.
 */
export abstract class TerrainFeature {
  protected scene: THREE.Scene;
  protected position: THREE.Vector3;
  protected rotation: number;
  protected mesh: THREE.Group;
  protected readonly terrainType: TerrainType;

  constructor(
    scene: THREE.Scene,
    position: THREE.Vector3,
    rotation: number,
    terrainType: TerrainType
  ) {
    this.scene = scene;
    this.position = position.clone();
    this.rotation = rotation;
    this.terrainType = terrainType;
    this.mesh = new THREE.Group();
    this.mesh.position.copy(this.position);
    this.mesh.rotation.y = this.rotation;
  }

  /**
   * Get the height at a world position.
   * Returns null if the position is outside this terrain feature.
   */
  abstract getHeightAtPosition(worldPos: THREE.Vector3): number | null;

  /**
   * Get the bounding box for spatial grid registration.
   */
  abstract getBounds(): TerrainBounds;

  /**
   * Get the terrain type identifier.
   */
  getType(): TerrainType {
    return this.terrainType;
  }

  /**
   * Get the center position of this terrain feature.
   */
  getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  /**
   * Transform a world position to local coordinates relative to this feature.
   */
  protected worldToLocal(worldPos: THREE.Vector3): THREE.Vector3 {
    const local = worldPos.clone().sub(this.position);

    // Apply inverse rotation if needed
    if (this.rotation !== 0) {
      const cos = Math.cos(-this.rotation);
      const sin = Math.sin(-this.rotation);
      const x = local.x * cos - local.z * sin;
      const z = local.x * sin + local.z * cos;
      local.x = x;
      local.z = z;
    }

    return local;
  }

  /**
   * Create the standard terrain material with synthwave styling.
   */
  protected createTerrainMaterial(
    color: number = TERRAIN_COLORS.BASE,
    emissive: number = 0x000000,
    emissiveIntensity: number = 0
  ): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity,
      metalness: 0.7,
      roughness: 0.6,
      flatShading: true, // Low-poly aesthetic
    });
  }

  /**
   * Create a glowing edge highlight material.
   */
  protected createEdgeMaterial(): THREE.MeshStandardMaterial {
    return new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.8,
    });
  }

  /**
   * Add the terrain mesh to the scene.
   */
  protected addToScene(): void {
    this.scene.add(this.mesh);
  }

  /**
   * Gaussian falloff function for smooth terrain height transitions.
   */
  protected gaussianFalloff(distance: number, radius: number): number {
    const sigma = radius / 3; // 3 sigma = ~99% of the gaussian
    return Math.exp(-(distance * distance) / (2 * sigma * sigma));
  }

  /**
   * Smooth step function for gradual transitions.
   */
  protected smoothstep(edge0: number, edge1: number, x: number): number {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  }

  /**
   * Clean up resources when destroyed.
   */
  destroy(): void {
    this.scene.remove(this.mesh);
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }
}
